import React, { useState, useEffect } from 'react';
import { withRouter, Link } from 'react-router-dom';
import { getLeague, getTeam, getPlayers, makeSubs } from '../../data';
import {
    CircularProgress, Button, TextField, MenuItem, Typography,
    Paper, Grid, IconButton, Chip,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import Autocomplete from '@material-ui/lab/Autocomplete';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import CheckIcon from '@material-ui/icons/Check';
import ClearIcon from '@material-ui/icons/Clear';

function SubRow({ teamName, teamData, playerBios, gameWeek, onSubChange, existingSub }) {
    const [outPlayer, setOutPlayer] = useState(existingSub?.out || '');
    const [inPlayer, setInPlayer] = useState(existingSub?.in || '');

    const prevGW = `gw${parseInt(gameWeek.replace('gw', '')) - 1}`;
    const currentXI = teamData?.[prevGW]?.players || [];
    const squad = teamData?.squad || [];

    const xiPlayerIds = currentXI.map(p => p.id);

    const getPlayerName = (id) => {
        const bio = playerBios.find(p => p.id === id);
        return bio ? bio.name : id;
    };

    const getPlayerInfo = (id) => {
        const bio = playerBios.find(p => p.id === id);
        return bio ? `${bio.name} (${bio.team} - ${bio.pos})` : id;
    };

    useEffect(() => {
        if (outPlayer && inPlayer) {
            onSubChange(teamName, outPlayer, inPlayer);
        } else if (!outPlayer && !inPlayer) {
            onSubChange(teamName, null, null);
        }
    }, [outPlayer, inPlayer]);

    const handleClear = () => {
        setOutPlayer('');
        setInPlayer('');
        onSubChange(teamName, null, null);
    };

    return (
        <Paper style={{ padding: 12, marginBottom: 8 }} variant="outlined" data-testid={`sub-row-${teamName}`}>
            <Grid container spacing={1} alignItems="center">
                <Grid item xs={12}>
                    <Typography variant="subtitle2">{teamName}</Typography>
                </Grid>
                <Grid item xs={5}>
                    <TextField
                        select
                        fullWidth
                        size="small"
                        label="Out (from XI)"
                        value={outPlayer}
                        onChange={(e) => setOutPlayer(e.target.value)}
                        SelectProps={{
                            SelectDisplayProps: { 'data-testid': `out-select-${teamName}` }
                        }}
                    >
                        <MenuItem value="">
                            <em>None</em>
                        </MenuItem>
                        {currentXI.map(p => (
                            <MenuItem key={p.id} value={p.id}>
                                {getPlayerInfo(p.id)}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid item xs={1} style={{ textAlign: 'center' }}>
                    <SwapHorizIcon color="action" />
                </Grid>
                <Grid item xs={5}>
                    <Autocomplete
                        options={playerBios}
                        getOptionLabel={(option) => `${option.name} (${option.team} - ${option.pos})`}
                        value={playerBios.find(p => p.id === inPlayer) || null}
                        onChange={(e, newValue) => setInPlayer(newValue ? newValue.id : '')}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="In"
                                size="small"
                                fullWidth
                                inputProps={{
                                    ...params.inputProps,
                                    'data-testid': `in-select-${teamName}`,
                                }}
                            />
                        )}
                        disablePortal
                        blurOnSelect
                    />
                </Grid>
                <Grid item xs={1}>
                    {(outPlayer || inPlayer) && (
                        <IconButton size="small" onClick={handleClear} data-testid={`clear-${teamName}`}
                            aria-label={`Clear substitution for ${teamName}`}>
                            <ClearIcon fontSize="small" />
                        </IconButton>
                    )}
                </Grid>
            </Grid>
            {outPlayer && inPlayer && (
                <Chip
                    size="small"
                    label={`${getPlayerName(outPlayer)} → ${getPlayerName(inPlayer)}`}
                    style={{ marginTop: 4 }}
                    data-testid={`sub-chip-${teamName}`}
                />
            )}
        </Paper>
    );
}

function SubsImproved(props) {
    const leagueID = props.match.params.leagueID;
    const embedded = props.embedded || false;

    const [loading, setLoading] = useState(true);
    const [league, setLeague] = useState(null);
    const [teams, setTeams] = useState([]);
    const [teamDataMap, setTeamDataMap] = useState({});
    const [playerBios, setPlayerBios] = useState([]);
    const [gameWeek, setGameWeek] = useState('');
    const [subs, setSubs] = useState({});
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const leagueData = await getLeague(leagueID);
                setLeague(leagueData);
                setTeams(leagueData.teams || []);
                const bios = await getPlayers(leagueData.tournament, leagueID);
                setPlayerBios(bios.players || []);
            } catch (err) {
                setError(err.message);
            }
            setLoading(false);
        };
        load();
    }, [leagueID]);

    const handleGameWeekChange = async (e) => {
        const gw = e.target.value;
        setGameWeek(gw);
        setSuccess(false);
        setError(null);
        setSubs({});

        // Load team data for each team to get their previous GW XI
        const dataMap = {};
        for (const team of teams) {
            try {
                const data = await getTeam(leagueID, team.name);
                dataMap[team.name] = data;
            } catch (err) {
                console.error(`Failed to load team ${team.name}`, err);
            }
        }
        setTeamDataMap(dataMap);
    };

    const handleSubChange = (teamName, outPlayer, inPlayer) => {
        setSubs(prev => {
            const next = { ...prev };
            if (outPlayer && inPlayer) {
                next[teamName] = { out: outPlayer, in: inPlayer };
            } else {
                delete next[teamName];
            }
            return next;
        });
        setSuccess(false);
    };

    const handleSave = async () => {
        if (!gameWeek) {
            setError('Select a gameweek');
            return;
        }
        const subsList = teams.map(team => {
            const sub = subs[team.name];
            if (sub) {
                return { squadName: team.name, out: sub.out, in: sub.in };
            } else {
                return { squadName: team.name, out: 'pass', in: 'pass' };
            }
        });

        setSaving(true);
        setError(null);
        try {
            await makeSubs(leagueID, gameWeek, subsList);
            setSuccess(true);
        } catch (err) {
            setError(err.message);
        }
        setSaving(false);
    };

    const subsCount = Object.keys(subs).length;

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}>
                <CircularProgress />
            </div>
        );
    }

    return (
        <div style={{ maxWidth: 600, margin: '0 auto', padding: 16, marginBottom: 60 }}>
            {!embedded && (
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                    <Link to={`/leagues/${leagueID}`} style={{ marginRight: 8 }}>
                        <ArrowBackIcon />
                    </Link>
                    <Typography variant="h5" style={{ flexGrow: 1 }}>
                        Substitutions
                    </Typography>
                </div>
            )}

            {error && <Alert severity="error" style={{ marginBottom: 16 }} data-testid="subs-error">{error}</Alert>}
            {success && <Alert severity="success" style={{ marginBottom: 16 }} data-testid="subs-success">Substitutions saved successfully</Alert>}

            <Grid container spacing={2} style={{ marginBottom: 16 }}>
                <Grid item xs={6}>
                    <TextField
                        select
                        fullWidth
                        label="Target Gameweek"
                        value={gameWeek}
                        onChange={handleGameWeekChange}
                        SelectProps={{
                            SelectDisplayProps: { 'data-testid': 'subs-gameweek-select' }
                        }}
                    >
                        {Array.from({ length: 15 }, (_, i) => `gw${i + 2}`).map(gw => (
                            <MenuItem key={gw} value={gw}>{gw.toUpperCase()}</MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid item xs={6} style={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                        {subsCount} sub{subsCount !== 1 ? 's' : ''} pending
                    </Typography>
                </Grid>
            </Grid>

            {gameWeek && teams.map(team => (
                <SubRow
                    key={team.name}
                    teamName={team.name}
                    teamData={teamDataMap[team.name]}
                    playerBios={playerBios}
                    gameWeek={gameWeek}
                    onSubChange={handleSubChange}
                    existingSub={subs[team.name]}
                />
            ))}

            {gameWeek && (
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={saving || subsCount === 0}
                    style={{ marginTop: 16 }}
                    data-testid="save-subs-button"
                    startIcon={saving ? <CircularProgress size={16} /> : null}
                >
                    {saving ? 'Saving...' : `Save Substitutions (${subsCount})`}
                </Button>
            )}
        </div>
    );
}

export default withRouter(SubsImproved);
